import React from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { MAIN_AREAS, CORE_SERVICES, COMPANY_INFO } from '../data/siteData';

// Verbatim bodies for the 6 primary city pages from scrape
const AREA_BODIES = {
  kolding: {
    heading: 'Professionel træpleje i Kolding: Fra slotshaven til din egen have',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traepleje-i-kolding.jpg',
    bodyHtml: `
      <p>Kolding er en by, hvor historien møder nutiden. Fra de gamle, majestætiske træer omkring Koldinghus og Slotssøen til de nyere villahavers unge beplantning, stiller byens træer vidt forskellige krav. Hærvejens Træpleje er dit lokale hold af certificerede specialister, når det kommer til professionel <strong>træpleje i Kolding</strong>. Vi forstår byens unikke karakter og har ekspertisen til at varetage enhver opgave, stor som lille, med sikkerhed og præcision.</p>
      <p>Uanset om du bor i centrum med en klemt baghave, i et af de større villakvarterer eller ude på landet, har vi udstyret og erfaringen til at hjælpe dig med at vedligeholde eller fjerne dine træer på den mest hensigtsmæssige måde.</p>
      <h2>En komplet løsning for din have i Kolding</h2>
      <p>Træpleje er mere end bare at fælde et træ. Det er en samlet proces, der sikrer, at opgaven bliver løst korrekt fra start til slut. Vi tilbyder en komplet palette af ydelser, der dækker alle behov:</p>
      <ul>
        <li><strong><a href="/traefaeldning-i-kolding">Træfældning i Kolding:</a></strong> Når et træ er blevet for stort, er sygt, eller står i vejen for nye planer, sørger vi for en sikker og kontrolleret fældning. Vi planlægger fældningen ned til mindste detalje, så den passer til netop din grund.</li>
        <li><strong><a href="/topkapning-kolding">Topkapning i Kolding:</a></strong> Står træet tæt på bygninger, el-ledninger eller andre forhindringer? Med topkapning fjerner vi træet sektion for sektion fra toppen og ned. Det er den ultimative sikre løsning på trange steder.</li>
        <li><strong><a href="/rodfraesning-i-kolding">Rodfræsning i Kolding:</a></strong> Når træet er fældet, står stubben tilbage. Vi fjerner den effektivt med en rodfræser, så du kan genetablere din græsplæne eller starte på et nyt haveprojekt med det samme.</li>
      </ul>
      <h2>Hvorfor vælge Hærvejens Træpleje?</h2>
      <p>I en by som Kolding, der fungerer som et centralt trafikknudepunkt, forstår vi vigtigheden af effektivitet og professionalisme. Vores hold på 12 mand er ikke bare erfarne, men også certificerede arborister. Det er din garanti for, at vi altid anvender de nyeste teknikker og overholder de strengeste sikkerhedsstandarder.</p>
      <p>Vi er fuldt forsikrede, så du kan være helt tryg, når vi arbejder på din grund. Vi behandler din have med respekt, rydder pænt op efter os og efterlader den klar til brug. For os er en veludført opgave ikke kun et fældet træ, men også en tilfreds kunde.</p>
      <h2>Rådgivning er en del af vores service</h2>
      <p>Er du i tvivl, om dit træ skal fældes, beskæres eller noget helt tredje? En del af professionel træpleje er også at give den rette rådgivning. Vi kommer gerne ud og vurderer dit træs tilstand og lytter til dine ønsker. Nogle gange kan en kraftig beskæring være nok til at give lys og luft, mens det i andre tilfælde er mest fornuftigt at fælde træet, før det udgør en risiko.</p>
      <p>Vi giver dig en ærlig og faglig vurdering, så du kan træffe den bedste beslutning for din have og din sikkerhed.</p>
    `,
  },
  fredericia: {
    heading: 'Træpleje i Fredericia: Ekspertise fra voldene til villahaven',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traepleje-i-fredericia-2.jpg',
    bodyHtml: `
      <p>Fredericia er en by med en helt særlig karakter. Anlagt som en fæstning, med de historiske volde som et grønt bånd om bykernen og de retvinklede gader, der vidner om en planlagt fortid. Denne unikke struktur afspejles i byens haver – fra de små, lukkede gårdhaver i centrum til de større villahaver uden for voldene. Hærvejens Træpleje tilbyder professionel <strong>træpleje i Fredericia</strong> med dyb respekt for byens historie og en faglighed, der kan løse enhver opgave.</p>
      <p>Uanset om du har et gammelt træ, der knejser over voldanlægget, eller et ungt træ i en nyere udstykning, der kræver opmærksomhed, har vores hold af certificerede arborister den nødvendige ekspertise. Vi kombinerer moderne teknikker med en forståelse for de særlige hensyn, der skal tages i en historisk by som Fredericia.</p>
      <h2>En komplet løsning for dine træer i fæstningsbyen</h2>
      <p>God træpleje handler om at have den rette løsning til enhver situation. Vi tilbyder en fuld servicepakke, der dækker alle aspekter af træarbejde, så du kun behøver at henvende dig ét sted:</p>
      <ul>
        <li><strong><a href="/traefaeldning-i-fredericia">Træfældning i Fredericia:</a></strong> Når et træ er blevet for stort, er sygt, eller står i vejen for nye planer, sørger vi for en sikker og kontrolleret fældning.</li>
        <li><strong><a href="/topkapning-fredericia">Topkapning i Fredericia:</a></strong> I de tætte gader inden for voldene er pladsen ofte trang. Her er topkapning den ideelle løsning, hvor vi fjerner træet sektion for sektion fra toppen og ned.</li>
        <li><strong><a href="/rodfraesning-i-fredericia">Rodfræsning i Fredericia:</a></strong> Når træet er væk, står stubben tilbage. Vi fjerner den effektivt med en rodfræser, så du kan genetablere din græsplæne eller starte på et nyt haveprojekt med det samme.</li>
      </ul>
      <h2>Sikkerhed og professionalisme i højsædet</h2>
      <p>At arbejde med store træer er en opgave, der kræver den ypperste professionalisme. Vores hold består af 12 erfarne og certificerede arborister, der alle er fuldt forsikrede. Det er din sikkerhed for, at arbejdet altid udføres efter de højeste standarder og med det rette sikkerhedsudstyr.</p>
    `,
  },
  vejle: {
    heading: 'Træpleje i Vejle: Eksperter i byens bakkede haver',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traefaeldning.jpg',
    bodyHtml: `
      <p>Fra de stejle skrænter i Munkebjergskoven til de charmerende villahaver i Bredballe – Vejle er en by, hvor naturen er en integreret del af bybilledet. Træerne i Vejle er ikke bare dekoration; de er levende skulpturer, der kræver viden og respekt. Hærvejens Træpleje tilbyder professionel <strong>træpleje i Vejle</strong>, udført af certificerede arborister, der forstår sig på de unikke udfordringer, som byens kuperede terræn giver.</p>
      <p>Vi er et lokalkendt team med en passion for træer. Vores mission er at hjælpe dig med at passe på dine træer, så de forbliver sunde, smukke og sikre i mange år fremover.</p>
      <h2>En komplet service til dine træer i Vejle</h2>
      <ul>
        <li><strong>Træfældning:</strong> Når et træ er sygt, dødt eller er blevet en sikkerhedsrisiko, er en kontrolleret fældning den rigtige løsning. Vi har stor erfaring med <a href="/traefaeldning-i-vejle">træfældning i Vejle</a>.</li>
        <li><strong>Topkapning:</strong> I mange af Vejles haver er pladsen trang. Her er <a href="/topkapning-vejle">topkapning i Vejle</a> den eneste sikre metode. Vores erfarne klatrere fjerner træet oppefra i mindre sektioner.</li>
        <li><strong>Beskæring:</strong> En korrekt beskæring kan forlænge et træs levetid, forbedre dets form og øge frugtsætningen.</li>
        <li><strong>Rodfræsning:</strong> For at gøre haven helt klar til nye projekter, tilbyder vi effektiv <a href="/rodfraesning-i-vejle">rodfræsning i Vejle</a>.</li>
      </ul>
      <h2>Hvorfor vælge en certificeret arborist?</h2>
      <p>At overlade sine træer til en fagmand er en tillidssag. En arborist er en træplejer med en formel uddannelse i træers biologi, vækst og sundhed. Når du vælger Hærvejens Træpleje, vælger du et team, der ikke bare fælder træer, men forstår dem.</p>
    `,
  },
  horsens: {
    heading: 'Professionel Træpleje i Horsens: Få hjælp af lokale arborister',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traepleje-i-horsens-hurtigt-tilbud-fra-din-lokale-arborist.jpg',
    bodyHtml: `
      <p>Har du træer i haven i Horsens, der er blevet for store, er syge, eller blot trænger til en professionel hånd? Hærvejens Træpleje er dit lokale team af certificerede arborister, der tilbyder komplet <strong>træpleje i Horsens</strong>. Vi forstår, at træerne i byens haver og parker er en vigtig del af bybilledet. De giver skønhed og karakter, men de kræver også korrekt vedligeholdelse for at forblive sunde og sikre for deres omgivelser.</p>
      <p>Vi dækker hele Horsens og omegn. Vores mission er at levere førsteklasses håndværk med fokus på sikkerhed og træets sundhed.</p>
      <h2>Vores ydelser inden for træpleje i Horsens</h2>
      <ul>
        <li><strong>Træfældning:</strong> Vi udfører <a href="/traefaeldning-i-horsens">træfældning i Horsens</a> med den største præcision, så træet falder sikkert og kontrolleret.</li>
        <li><strong>Topkapning af træer:</strong> Står dit træ klemt inde mellem huset og naboens garage? Så er <a href="/topkapning-horsens">topkapning i Horsens</a> den eneste forsvarlige løsning.</li>
        <li><strong>Rodfræsning og stubfræsning:</strong> Med professionel <a href="/rodfraesning-i-horsens">rodfræsning i Horsens</a> løser vi problemet effektivt og efterlader din have pæn og plan.</li>
        <li><strong>Beskæring:</strong> Vi udfører kronereduktion, udtynding og formklipning.</li>
      </ul>
    `,
  },
  skanderborg: {
    heading: 'Professionel Træpleje i Skanderborg og omegn',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traepleje-i-skanderborg.jpg',
    bodyHtml: `
      <p>Har du træer i haven i Skanderborg, der trænger til en kærlig og professionel hånd? Hærvejens Træpleje er dit lokale hold af certificerede arborister, der tilbyder komplet <strong>træpleje i Skanderborg</strong>. Vi ved, at træerne omkring de smukke søer og i byens haver er en vigtig del af områdets charme. Derfor kræver de korrekt vedligeholdelse for at forblive sunde, sikre og smukke i mange år fremover.</p>
      <p>Vi dækker hele Skanderborg Kommune med fokus på sikkerhed og træets biologi.</p>
      <h2>Vores ydelser inden for træpleje i Skanderborg</h2>
      <ul>
        <li><strong>Træfældning:</strong> Vi udfører <a href="/traefaeldning-i-skanderborg">træfældning i Skanderborg</a> med præcision og grundig planlægning.</li>
        <li><strong>Topkapning af træer:</strong> Ved stejle skråninger og tæt bebyggelse er <a href="/topkapning-skanderborg">topkapning i Skanderborg</a> den eneste forsvarlige metode.</li>
        <li><strong>Rodfræsning og stubfræsning:</strong> Effektiv <a href="/rodfraesning-i-skanderborg">rodfræsning i Skanderborg</a> fjerner stubben skånsomt for haven.</li>
        <li><strong>Beskæring:</strong> Korrekt beskæring forbedrer træets struktur og giver lys og luft.</li>
      </ul>
    `,
  },
  aarhus: {
    heading: 'Professionel Træpleje i Aarhus: Få hjælp af uddannede arborister',
    image: 'https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/traepleje-i-aarhus.jpg',
    bodyHtml: `
      <p>Har du træer i haven, der kræver professionel opmærksomhed? Hærvejens Træpleje er dit lokale team af certificerede arborister, der tilbyder komplet <strong>træpleje i Aarhus</strong>. Vi forstår, at træer er en vigtig del af din have. De giver skønhed, skygge og karakter. Men de kræver også korrekt vedligeholdelse for at forblive sunde, sikre og smukke.</p>
      <p>Vi dækker hele Aarhus og omegn med fokus på sikkerhed og faglighed.</p>
      <h2>Vores ydelser i Aarhus</h2>
      <ul>
        <li><strong>Træfældning i Aarhus:</strong> Udføres med den største præcision via kontrolleret fald. Se mere om <a href="/traefaeldning-i-aarhus">træfældning i Aarhus</a>.</li>
        <li><strong>Topkapning af træer:</strong> Den sikre løsning i tæt bebyggede villakvarterer. Se mere om <a href="/topkapning-aarhus">topkapning i Aarhus</a>.</li>
        <li><strong>Rodfræsning og stubfræsning:</strong> Fjerner stubben effektivt. Læs mere om <a href="/rodfraesning-aarhus">rodfræsning i Aarhus</a>.</li>
        <li><strong>Beskæring:</strong> Kronereduktion, udtynding og frugttræer.</li>
      </ul>
      <p>Hos Hærvejens Træpleje får du certificerede arborister fra Skovskolen, fuld ansvarsforsikring op til 10 mio. kr., lokalt kendskab til Aarhus' kvarterer og ærlig, gennemsigtig rådgivning.</p>
    `,
  },
};

export default function AreaDetailPage({ forcedSlug }) {
  const { slug } = useParams();
  const location = useLocation();

  const currentSlug = forcedSlug || slug || location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const area = MAIN_AREAS.find((a) => a.slug === currentSlug);
  const content = AREA_BODIES[currentSlug];

  if (!area || !content) {
    return <Navigate to="/omraader" replace />;
  }

  const nearbyAreas = MAIN_AREAS.filter((a) => area.nearbySlugs.includes(a.slug));

  return (
    <>
      <SEO
        title={area.seoTitle}
        description={area.metaDesc}
        ogImage={content.image || area.image}
        canonical={`https://xn--hrvejenstrpleje-xlbj.dk/${area.slug}/`}
      />

      <Breadcrumbs
        items={[
          { label: 'Områder', href: '/omraader' },
          { label: area.name },
        ]}
      />

      {/* Hero Header */}
      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={content.image} alt={`Træpleje i ${area.name}`} className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            {area.region}
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            {content.heading}
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            {area.metaDesc}
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main prose */}
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-2xl overflow-hidden border border-[#dce5df] bg-white shadow-xs">
              <img
                src={content.image}
                alt={`Træpleje i ${area.name}`}
                className="w-full max-h-[460px] object-cover"
              />
            </div>

            <div
              className="article-prose bg-white p-6 sm:p-10 rounded-2xl border border-[#dce5df] shadow-xs"
              dangerouslySetInnerHTML={{ __html: content.bodyHtml }}
            />

            {/* 3 Dedicated Landing Pages for this City */}
            <div className="bg-[#f2f7f4] rounded-2xl p-6 sm:p-8 border border-[#cfe0d6]">
              <h3 className="text-xl font-bold text-[#14291d] mb-2">
                Specialiserede fældnings- og stubfræsningssider i {area.name}
              </h3>
              <p className="text-sm text-[#475b4f] mb-6">
                Læs detaljeret om de enkelte opgaver, vores fremgangsmåde og priser i netop {area.name}:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {area.localPages.map((lp) => (
                  <Link
                    key={lp.slug}
                    to={`/${lp.slug}`}
                    className="p-4 rounded-xl bg-white border border-[#d4e1d9] hover:border-[#2d6a4f] shadow-2xs hover:shadow-xs transition-all flex flex-col group"
                  >
                    <span className="text-xs font-semibold text-[#527a65] mb-1">
                      {lp.service}
                    </span>
                    <h4 className="font-bold text-sm text-[#14291d] group-hover:text-[#2d6a4f] transition-colors mb-2">
                      {lp.title}
                    </h4>
                    <span className="mt-auto text-xs font-semibold text-[#1b4332] flex items-center gap-1">
                      <span>Læs mere</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Neighbouring Areas */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#dce5df]">
              <h3 className="text-lg font-bold text-[#14291d] mb-4">
                Vi servicerer også tilstødende områder og byer:
              </h3>
              <div className="flex flex-wrap gap-2.5">
                {nearbyAreas.map((na) => (
                  <Link
                    key={na.slug}
                    to={`/${na.slug}`}
                    className="px-4 py-2 rounded-xl bg-[#f5f8f6] border border-[#d2ded6] text-xs sm:text-sm font-semibold text-[#25392d] hover:bg-[#1b4332] hover:text-white transition-colors"
                  >
                    Træpleje i {na.name} »
                  </Link>
                ))}
                <Link
                  to="/omraader"
                  className="px-4 py-2 rounded-xl bg-[#e8f1ec] text-xs sm:text-sm font-semibold text-[#1b4332] hover:underline"
                >
                  Se samlet områdehub »
                </Link>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-[#143627] text-white rounded-2xl p-6 shadow-sm">
              <span className="text-xs font-semibold text-[#7dd2a6]">
                Lokal service i {area.name}
              </span>
              <h3 className="text-xl font-bold mt-1 mb-3">Har du brug for hjælp til et træ?</h3>
              <p className="text-sm text-[#c8ded1] mb-6 leading-relaxed">
                Vi kommer gerne forbi og besigtiger dit træ i {area.name} og omegn – gratis og uforpligtende.
              </p>
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-white text-[#122b1f] font-bold text-sm hover:bg-[#f2f7f4] transition-colors whitespace-nowrap mb-3"
              >
                <i className="fa-solid fa-phone text-[#2d6a4f]" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring {COMPANY_INFO.phone}</span>
              </a>
              <a
                href="#form-navn"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('form-navn')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors cursor-pointer"
              >
                <span>Gå til formular »</span>
              </a>
            </div>

            {/* Core Services link list */}
            <div className="bg-white rounded-2xl p-6 border border-[#dce5df] shadow-xs">
              <h4 className="text-base font-bold text-[#14291d] mb-4">Vores ydelser i {area.name}</h4>
              <ul className="space-y-2">
                {CORE_SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/${s.slug}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-[#f2f7f4] text-xs sm:text-sm text-[#273a2f]"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`fa-solid ${s.icon} text-[#357959] w-4 text-center`} aria-hidden="true" />
                        <span>{s.title}</span>
                      </span>
                      <i className="fa-solid fa-chevron-right text-[10px] text-[#8ea396]" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
